import type { FilterFeatureOptions, FiltersState } from '~/client/common/filters'
import { isVenueTrustSignalDomain, type VenueTrustSignalDomain } from '~/shared/venue/trust-signals'
import { DEFAULT_SPACE_SORT_BY, SPACE_SORT_OPTIONS, type SpaceSortBy } from '~/shared/space/space-list.params'

// ── URL Search Params (flat structure for URL) ──────────────────────────────
export type ListingsSearchParams = {
  location?: string
  locationPlaceLabel?: string
  vibe?: string
  locationLat?: number
  locationLng?: number
  radiusMiles?: number
  standingGuestCount?: number
  seatedGuestCount?: number
  fbMin?: number
  fbMax?: number
  venueName?: string
  googlePlaceId?: string
  boundsNwGeohash?: string
  boundsSeGeohash?: string
  page?: number
  sortBy?: SpaceSortBy
  // Multi-select as comma-separated strings
  spaceTypes?: string
  privacyTypes?: string
  noisePrivacy?: string
  amenities?: string
  av?: string
  outdoor?: string
  windows?: string
  pricePoints?: string
  google?: string
  michelin?: string
  venueTypes?: string
  venueHighlights?: string
  editorialRecognition?: string
  topHospitalityGroups?: boolean
  cuisines?: string
  neighborhoods?: string
  venueTags?: string
}

type ListingsFilterSearchKey = Exclude<keyof ListingsSearchParams, 'page' | 'sortBy'>

const LISTINGS_FILTER_SEARCH_KEYS: ListingsFilterSearchKey[] = [
  'location',
  'locationPlaceLabel',
  'vibe',
  'locationLat',
  'locationLng',
  'radiusMiles',
  'standingGuestCount',
  'seatedGuestCount',
  'fbMin',
  'fbMax',
  'venueName',
  'googlePlaceId',
  'boundsNwGeohash',
  'boundsSeGeohash',
  'spaceTypes',
  'privacyTypes',
  'noisePrivacy',
  'amenities',
  'av',
  'outdoor',
  'windows',
  'pricePoints',
  'google',
  'michelin',
  'venueTypes',
  'venueHighlights',
  'editorialRecognition',
  'topHospitalityGroups',
  'cuisines',
  'neighborhoods',
  'venueTags',
]

// ── Multi-select helpers ────────────────────────────────────────────────────

function parseMultiSelect(value: string | undefined): Record<string, boolean> {
  if (!value) return {}
  return value.split(',').reduce(
    (acc, key) => {
      const k = key.trim()
      if (k) acc[k] = true
      return acc
    },
    {} as Record<string, boolean>,
  )
}

function parseMultiSelectNumeric(value: string | undefined): Record<number, boolean> {
  if (!value) return {}
  return value.split(',').reduce(
    (acc, key) => {
      const num = parseInt(key.trim(), 10)
      if (!isNaN(num)) acc[num] = true
      return acc
    },
    {} as Record<number, boolean>,
  )
}

function parseVenueTrustSignals(value: string | undefined): Partial<Record<VenueTrustSignalDomain, boolean>> {
  if (!value) return {}
  return value.split(',').reduce(
    (acc, key) => {
      const trimmed = key.trim()
      if (isVenueTrustSignalDomain(trimmed)) acc[trimmed] = true
      return acc
    },
    {} as Partial<Record<VenueTrustSignalDomain, boolean>>,
  )
}

function parseBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return undefined
  if (value === 'true' || value === '1') return true
  if (value === 'false' || value === '0') return false
  return undefined
}

function serializeMultiSelect(record: Record<string, boolean> | undefined): string | undefined {
  if (!record) return undefined
  const selected = Object.entries(record)
    .filter(([, v]) => v)
    .map(([k]) => k)
  return selected.length > 0 ? selected.join(',') : undefined
}

function serializeMultiSelectNumeric(record: Record<number, boolean> | undefined): string | undefined {
  if (!record) return undefined
  const selected = Object.entries(record)
    .filter(([, v]) => v)
    .map(([k]) => k)
  return selected.length > 0 ? selected.join(',') : undefined
}

function parseSortBy(value: unknown): SpaceSortBy | undefined {
  return typeof value === 'string' && SPACE_SORT_OPTIONS.includes(value as SpaceSortBy)
    ? (value as SpaceSortBy)
    : undefined
}

// ── Converters ──────────────────────────────────────────────────────────────

/** Convert URL search params → FiltersState (returns null when no filter is set) */
export function urlParamsToFilters(params: ListingsSearchParams): FiltersState | null {
  const hasAnyFilter = LISTINGS_FILTER_SEARCH_KEYS.some((key) => {
    const value = params[key]
    if (typeof value === 'boolean') return value
    return value !== undefined && value !== ''
  })
  if (!hasAnyFilter) return null

  return {
    location: params.location || '',
    locationPlaceLabel: params.locationPlaceLabel,
    vibe: params.vibe || '',
    locationLat: params.locationLat,
    locationLng: params.locationLng,
    radiusMiles: params.radiusMiles,
    standingGuestCount: params.standingGuestCount,
    seatedGuestCount: params.seatedGuestCount,
    fbMin: params.fbMin,
    fbMax: params.fbMax,
    venueName: params.venueName || '',
    googlePlaceId: params.googlePlaceId,
    boundsNwGeohash: params.boundsNwGeohash,
    boundsSeGeohash: params.boundsSeGeohash,
    spaceTypes: parseMultiSelect(params.spaceTypes),
    privacyTypes: parseMultiSelect(params.privacyTypes),
    noisePrivacy: parseMultiSelect(params.noisePrivacy),
    amenities: parseMultiSelect(params.amenities),
    av: parseMultiSelect(params.av),
    outdoor: parseMultiSelect(params.outdoor),
    windows: parseMultiSelect(params.windows),
    pricePoints: parseMultiSelectNumeric(params.pricePoints),
    google: parseMultiSelect(params.google),
    michelin: parseMultiSelect(params.michelin),
    venueTypes: parseMultiSelect(params.venueTypes),
    venueHighlights: parseMultiSelect(params.venueHighlights),
    editorialRecognition: parseVenueTrustSignals(params.editorialRecognition),
    topHospitalityGroups: params.topHospitalityGroups === true,
    cuisines: parseMultiSelect(params.cuisines),
    neighborhoods: parseMultiSelect(params.neighborhoods),
    venueTags: parseMultiSelectNumeric(params.venueTags),
  }
}

/** Convert FiltersState → URL search params (strips dynamic mappings) */
export function filtersToUrlParams(filters: FiltersState | null, options?: FilterFeatureOptions): ListingsSearchParams {
  const includeHospitalityGroupsFilter = options?.includeHospitalityGroupsFilter ?? true
  if (!filters) {
    return {
      location: undefined,
      locationPlaceLabel: undefined,
      vibe: undefined,
      locationLat: undefined,
      locationLng: undefined,
      radiusMiles: undefined,
      standingGuestCount: undefined,
      seatedGuestCount: undefined,
      fbMin: undefined,
      fbMax: undefined,
      venueName: undefined,
      googlePlaceId: undefined,
      boundsNwGeohash: undefined,
      boundsSeGeohash: undefined,
      spaceTypes: undefined,
      privacyTypes: undefined,
      noisePrivacy: undefined,
      amenities: undefined,
      av: undefined,
      outdoor: undefined,
      windows: undefined,
      pricePoints: undefined,
      google: undefined,
      michelin: undefined,
      venueTypes: undefined,
      venueHighlights: undefined,
      editorialRecognition: undefined,
      topHospitalityGroups: undefined,
      cuisines: undefined,
      neighborhoods: undefined,
      venueTags: undefined,
    }
  }
  return {
    location: filters.location || undefined,
    locationPlaceLabel: filters.locationPlaceLabel || undefined,
    vibe: filters.vibe || undefined,
    locationLat: filters.locationLat,
    locationLng: filters.locationLng,
    radiusMiles: filters.radiusMiles,
    standingGuestCount: filters.standingGuestCount,
    seatedGuestCount: filters.seatedGuestCount,
    fbMin: filters.fbMin,
    fbMax: filters.fbMax,
    venueName: filters.venueName || undefined,
    googlePlaceId: filters.googlePlaceId,
    boundsNwGeohash: filters.boundsNwGeohash,
    boundsSeGeohash: filters.boundsSeGeohash,
    spaceTypes: serializeMultiSelect(filters.spaceTypes),
    privacyTypes: serializeMultiSelect(filters.privacyTypes),
    noisePrivacy: serializeMultiSelect(filters.noisePrivacy),
    amenities: serializeMultiSelect(filters.amenities),
    av: serializeMultiSelect(filters.av),
    outdoor: serializeMultiSelect(filters.outdoor),
    windows: serializeMultiSelect(filters.windows),
    pricePoints: serializeMultiSelectNumeric(filters.pricePoints),
    google: serializeMultiSelect(filters.google),
    michelin: serializeMultiSelect(filters.michelin),
    venueTypes: serializeMultiSelect(filters.venueTypes),
    venueHighlights: serializeMultiSelect(filters.venueHighlights),
    editorialRecognition: serializeMultiSelect(filters.editorialRecognition),
    topHospitalityGroups: includeHospitalityGroupsFilter && filters.topHospitalityGroups ? true : undefined,
    cuisines: serializeMultiSelect(filters.cuisines),
    neighborhoods: serializeMultiSelect(filters.neighborhoods),
    venueTags: serializeMultiSelectNumeric(filters.venueTags),
  }
}

export function sortByToUrlParam(sortBy: SpaceSortBy): SpaceSortBy | undefined {
  return sortBy === DEFAULT_SPACE_SORT_BY ? undefined : sortBy
}

/** Validate raw search object into typed ListingsSearchParams */
export function validateSearchParams(search: Record<string, unknown>): ListingsSearchParams {
  return {
    location: typeof search.location === 'string' ? search.location : undefined,
    locationPlaceLabel: typeof search.locationPlaceLabel === 'string' ? search.locationPlaceLabel : undefined,
    vibe: typeof search.vibe === 'string' ? search.vibe : undefined,
    locationLat: typeof search.locationLat === 'number' ? search.locationLat : undefined,
    locationLng: typeof search.locationLng === 'number' ? search.locationLng : undefined,
    radiusMiles: typeof search.radiusMiles === 'number' ? search.radiusMiles : undefined,
    standingGuestCount: typeof search.standingGuestCount === 'number' ? search.standingGuestCount : undefined,
    seatedGuestCount: typeof search.seatedGuestCount === 'number' ? search.seatedGuestCount : undefined,
    fbMin: typeof search.fbMin === 'number' ? search.fbMin : undefined,
    fbMax: typeof search.fbMax === 'number' ? search.fbMax : undefined,
    venueName: typeof search.venueName === 'string' ? search.venueName : undefined,
    googlePlaceId: typeof search.googlePlaceId === 'string' ? search.googlePlaceId : undefined,
    boundsNwGeohash: typeof search.boundsNwGeohash === 'string' ? search.boundsNwGeohash : undefined,
    boundsSeGeohash: typeof search.boundsSeGeohash === 'string' ? search.boundsSeGeohash : undefined,
    spaceTypes: typeof search.spaceTypes === 'string' ? search.spaceTypes : undefined,
    privacyTypes: typeof search.privacyTypes === 'string' ? search.privacyTypes : undefined,
    noisePrivacy: typeof search.noisePrivacy === 'string' ? search.noisePrivacy : undefined,
    amenities: typeof search.amenities === 'string' ? search.amenities : undefined,
    av: typeof search.av === 'string' ? search.av : undefined,
    outdoor: typeof search.outdoor === 'string' ? search.outdoor : undefined,
    windows: typeof search.windows === 'string' ? search.windows : undefined,
    pricePoints: typeof search.pricePoints === 'string' ? search.pricePoints : undefined,
    google: typeof search.google === 'string' ? search.google : undefined,
    michelin: typeof search.michelin === 'string' ? search.michelin : undefined,
    venueTypes: typeof search.venueTypes === 'string' ? search.venueTypes : undefined,
    venueHighlights: typeof search.venueHighlights === 'string' ? search.venueHighlights : undefined,
    editorialRecognition: typeof search.editorialRecognition === 'string' ? search.editorialRecognition : undefined,
    topHospitalityGroups: parseBoolean(search.topHospitalityGroups),
    cuisines: typeof search.cuisines === 'string' ? search.cuisines : undefined,
    neighborhoods: typeof search.neighborhoods === 'string' ? search.neighborhoods : undefined,
    venueTags: typeof search.venueTags === 'string' ? search.venueTags : undefined,
    page: typeof search.page === 'number' ? search.page : undefined,
    sortBy: parseSortBy(search.sortBy),
  }
}
